

import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, CircularProgress, Box, TablePagination, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { attendanceApi } from '../api/attendanceApi';
import { employeeApi } from '../api/employeeApi';

interface Employee {
  id: string;
  employeeCode: string;
  fullName: string;
  attendanceStatus: string;
}


interface AttendanceStatusListProps {
  open: boolean;
  status: 'present' | 'absent';
  date: string;
  onClose: () => void;
}

const AttendanceStatusList: React.FC<AttendanceStatusListProps> = ({ open, status, date, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [search, setSearch] = useState("");
  // Filter employees by search (code or name)
  const filteredEmployees = employees.filter((emp: Employee) =>
    emp.employeeCode.toLowerCase().includes(search.toLowerCase()) ||
    emp.fullName.toLowerCase().includes(search.toLowerCase())
  );
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Helper to extract error message safely
  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e && 'message' in e && typeof (e as any).message === 'string') {
      return (e as any).message;
    }
    return String(e);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.getStatusList(date, status);
      // API returns array of { Id, EmployeeCode, FullName }
      setEmployees(
        (data || []).map((emp: any) => ({
          id: emp.id || emp.Id,
          employeeCode: emp.employeeCode || emp.EmployeeCode,
          fullName: emp.fullName || emp.FullName,
          attendanceStatus: status === 'present' ? 'Present' : 'Absent',
        }))
      );
    } catch (err: any) {
      setError('Failed to load attendance data: ' + getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Only fetch when dialog is opened
  React.useEffect(() => {
    if (open && status && date) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status, date]);

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date as dd-MMM-yyyy
  const formatDate = (isoDate: string) => {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Reset to first page when search changes
  React.useEffect(() => { setPage(0); }, [search]);

return (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <span>
        {status === 'present' ? 'Present' : 'Absent'} Employees on: {formatDate(date)}
      </span>
      <TextField
        size="small"
        variant="outlined"
        placeholder="Search by code or name"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 220, background: '#fff', borderRadius: 1 }}
      />
    </DialogTitle>
    <DialogContent dividers>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box>
          <Typography color="error">{error}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please check your network connection, API server, and authentication.<br />
            If this persists, check the browser console for more details.
          </Typography>
        </Box>
      ) : filteredEmployees.length === 0 ? (
        <Typography>No employees found.</Typography>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              maxHeight: rowsPerPage > 5 ? 360 : 'none',
              overflowY: rowsPerPage > 5 ? 'auto' : 'visible',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee Code</strong></TableCell>
                  <TableCell><strong>Employee Name</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell>{emp.employeeCode}</TableCell>
                    <TableCell>{emp.fullName}</TableCell>
                    <TableCell>
                      <Chip label={emp.attendanceStatus} color={emp.attendanceStatus === 'Present' ? 'success' : 'error'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredEmployees.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);
};

export default AttendanceStatusList;
